// Autocapture attributes with a closed vocabulary. Autocapture masks all
// text and attributes, so `data-ph-capture-attribute-*` values are the only
// meaning an autocaptured click carries. This helper is the one place that
// writes them, and it refuses anything outside surfaceVocabulary.js: the
// attribute is omitted, and in dev a warning names the value once so the
// vocabulary gets extended. It never throws, because an analytics attribute
// must not be able to break a render.
import { ATTRIBUTE, ATTRIBUTE_VOCABULARY, isKnownValue } from "./surfaceVocabulary.js";

const ATTRIBUTE_PREFIX = "data-ph-capture-attribute-";

const ATTRIBUTE_NAMES = Object.freeze({
    [ATTRIBUTE.DIALOG]: `${ATTRIBUTE_PREFIX}dialog`,
    [ATTRIBUTE.ACTION]: `${ATTRIBUTE_PREFIX}action`,
    [ATTRIBUTE.NAV]: `${ATTRIBUTE_PREFIX}nav`,
    [ATTRIBUTE.NAV_MODE]: `${ATTRIBUTE_PREFIX}nav-mode`,
    [ATTRIBUTE.TAB_KIND]: `${ATTRIBUTE_PREFIX}tab-kind`,
    [ATTRIBUTE.DIM]: `${ATTRIBUTE_PREFIX}dim`,
});

// The SDK lifts each `data-ph-capture-attribute-<name>` onto the event as a
// property called `<name>`. These are the property names an autocaptured
// click carries, each with the vocabulary its value must come from, which is
// what the event registry declares for `$autocapture`.
export const AUTOCAPTURE_PROPERTY_VALUES = Object.freeze(Object.fromEntries(
    Object.entries(ATTRIBUTE_NAMES).map(([kind, name]) => [name.slice(ATTRIBUTE_PREFIX.length), ATTRIBUTE_VOCABULARY[kind]]),
));

function isDev() {
    return Boolean(typeof import.meta !== "undefined" && import.meta.env?.DEV);
}

const warned = new Set();

function warnOnce(dev, warn, key, message) {
    if (!dev || warned.has(key)) return;
    warned.add(key);
    warn(`[analytics] ${message}`);
}

// Single attribute value for JSX that writes the attribute explicitly, which
// render-path files must do because the render lint forbids spread props
// built elsewhere. Returns undefined so React omits the attribute.
export function phAttr(kind, value, opts = {}) {
    const attrs = phAttrs({ [kind]: value }, opts);
    return attrs[ATTRIBUTE_NAMES[kind]];
}

export function phAttrs(spec, { dev = isDev(), warn = (...args) => console.warn(...args) } = {}) {
    const out = {};
    if (!spec) return out;
    for (const [kind, value] of Object.entries(spec)) {
        if (value === null || value === undefined) continue;
        const name = ATTRIBUTE_NAMES[kind];
        if (!name) {
            warnOnce(dev, warn, `kind:${kind}`, `phAttrs: unknown attribute kind "${kind}"; omitted`);
            continue;
        }
        if (!isKnownValue(kind, value)) {
            warnOnce(dev, warn, `${kind}:${value}`, `phAttrs: "${value}" is not in the ${kind} vocabulary (lib/analytics/surfaceVocabulary.js); omitted`);
            continue;
        }
        out[name] = String(value);
    }
    return out;
}

export function _resetWarningsForTests() {
    warned.clear();
}
